import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, ShieldCheck } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAdminMfa } from "@/hooks/useAdminMfa";

/**
 * Mounted once inside the admin area. When the backend refuses a sensitive
 * action because it needs a fresh authenticator code, this asks for the code
 * and then completes the original action automatically — no lost work.
 */
export default function AdminStepUpInterceptor() {
  const { stepUp, refresh, status } = useAdminMfa();
  const notEnrolled = status ? !status.enrolled : false;
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const resolverRef = useRef<((code: string | null) => void) | null>(null);

  useEffect(() => {
    const originalFetch = window.fetch.bind(window);

    const askForCode = () =>
      new Promise<string | null>((resolve) => {
        resolverRef.current = resolve;
        setCode("");
        setOpen(true);
      });

    window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
      const response = await originalFetch(input as any, init);
      if (response.status !== 403) return response;

      const url = typeof input === "string" ? input : input instanceof URL ? input.toString() : input.url;
      if (!url.includes("/functions/v1/")) return response;

      let body: any = null;
      try {
        body = await response.clone().json();
      } catch {
        return response;
      }
      if (body?.code !== "step_up_required") return response;

      const confirmed = await askForCode();
      if (!confirmed) return response;

      // Code already verified by the dialog — replay the original request.
      return await originalFetch(input as any, init);
    };

    return () => { window.fetch = originalFetch; };
  }, []);

  const finish = (value: string | null) => {
    resolverRef.current?.(value);
    resolverRef.current = null;
    setOpen(false);
    setCode("");
  };

  const confirm = async () => {
    setBusy(true);
    try {
      await stepUp(code.trim(), "sensitive_action");
      await refresh();
      finish(code.trim());
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) finish(null); }}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <div className="mx-auto mb-2 p-3 rounded-full bg-primary/10 w-fit">
            <ShieldCheck className="h-6 w-6 text-primary" />
          </div>
          <DialogTitle className="text-center">
            {notEnrolled ? "Set up your authenticator" : "Confirm this action"}
          </DialogTitle>
          <DialogDescription className="text-center">
            {notEnrolled
              ? "Sensitive actions need a code from an authenticator app, and you haven't set one up yet."
              : "This is a sensitive change, so enter the current code from your authenticator app."}
          </DialogDescription>
        </DialogHeader>
        <div className={notEnrolled ? "hidden" : "space-y-1"}>
          <Label htmlFor="global-stepup-code">6-digit code</Label>
          <Input
            id="global-stepup-code"
            autoFocus
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            onKeyDown={(e) => { if (e.key === "Enter" && code.length === 6) confirm(); }}
            placeholder="000000"
            className="text-center text-lg tracking-[0.4em] font-mono"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => finish(null)} disabled={busy}>Cancel</Button>
          {notEnrolled ? (
            <Button
              onClick={() => {
                finish(null);
                window.location.assign("/admin/my-security");
              }}
            >
              Set it up now
            </Button>
          ) : (
            <Button onClick={confirm} disabled={busy || code.length !== 6}>
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Confirm and continue
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
